import { Controller } from "@hotwired/stimulus"

// Neutraler §40-SGB-XI-Zuschuss-Rechner der Treppenlift-/Badumbau-Pillar-Seiten.
// Alle Daten kommen server-seitig als JSON (ProductPillarsController#zuschuss_data),
// gerechnet wird nur hier. Ergebnis erscheint ohne Lead-Formular — das ist der
// bewusste Unterschied zu den Lead-gated Preis-Schätzern (Cms::*CalculatorComponent).
export default class extends Controller {
  static values = { data: Object }

  static targets = [
    "pflegegradButton", "personenButton", "massnahmeSelect",
    "zuschuss", "zuschussHint", "noPgNote",
    "kostenRange", "eigenanteilRange",
    "plzInput", "plzResult", "landesHint"
  ]

  connect() {
    this.formatter = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
    this.selectedPflegegrad = 2
    this.selectedPersonen = 1
    this.highlight(this.pflegegradButtonTargets, 2)
    this.highlight(this.personenButtonTargets, 1)
    this.calculate()
  }

  selectPflegegrad(event) {
    this.selectedPflegegrad = parseInt(event.currentTarget.dataset.value)
    this.highlight(this.pflegegradButtonTargets, this.selectedPflegegrad)
    this.calculate()
  }

  selectPersonen(event) {
    this.selectedPersonen = parseInt(event.currentTarget.dataset.value)
    this.highlight(this.personenButtonTargets, this.selectedPersonen)
    this.calculate()
  }

  highlight(buttons, value) {
    buttons.forEach(btn => {
      const isSelected = parseInt(btn.dataset.value) === value
      btn.setAttribute("aria-pressed", String(isSelected))
      btn.classList.toggle("border-ph24_new_blue", isSelected)
      btn.classList.toggle("bg-ph24_new_blue/5", isSelected)
      btn.classList.toggle("ring-2", isSelected)
      btn.classList.toggle("ring-ph24_new_blue", isSelected)
      btn.classList.toggle("border-gray-200", !isSelected)
      btn.classList.toggle("hover:border-ph24_new_blue/40", !isSelected)
    })
  }

  calculate() {
    const data = this.dataValue
    const hasPflegegrad = this.selectedPflegegrad > 0
    const personen = Math.min(this.selectedPersonen, data.max_personen)
    const zuschuss = hasPflegegrad ? data.zuschuss_pro_person * personen : 0

    this.zuschussTarget.textContent = this.formatter.format(zuschuss)
    if (this.hasNoPgNoteTarget) this.noPgNoteTarget.classList.toggle("hidden", hasPflegegrad)

    if (this.hasZuschussHintTarget) {
      let hint = ""
      if (hasPflegegrad && personen === 1) {
        hint = `${this.formatter.format(data.zuschuss_pro_person)} je Maßnahme ab Pflegegrad 1, pro pflegebedürftiger Person im Haushalt.`
      } else if (hasPflegegrad && personen === 2) {
        hint = `${this.formatter.format(zuschuss)} für Sie beide: Der Zuschuss gilt pro Person mit Pflegegrad, bei einem Ehepaar mit zwei Pflegegraden verdoppelt er sich für dieselbe Maßnahme.`
      } else if (hasPflegegrad) {
        hint = `Bei ${personen} Berechtigten im Haushalt kumuliert der Zuschuss auf ${this.formatter.format(zuschuss)} (Deckel: ${this.formatter.format(data.zuschuss_pro_person * data.max_personen)} bei ${data.max_personen} Personen).`
      }
      this.zuschussHintTarget.textContent = hint
    }

    const massnahme = data.massnahmen[this.massnahmeSelectTarget.value]
    if (!massnahme) return
    const eigenanteilMax = Math.max(0, massnahme.max - zuschuss)
    this.kostenRangeTarget.textContent = this.formatRange(massnahme.min, massnahme.max)
    this.eigenanteilRangeTarget.textContent = this.formatRange(
      Math.max(0, massnahme.min - zuschuss),
      eigenanteilMax
    )
    // Bleibt ein Eigenanteil, auf mögliche Landesförderung hinweisen
    // (Landes-Daten liegen für Badumbau und Treppenlift vor).
    if (this.hasLandesHintTarget) {
      this.landesHintTarget.classList.toggle("hidden", eigenanteilMax === 0)
    }
  }

  // "0 € – 0 €" (or any equal bounds) collapses to a single value
  formatRange(min, max) {
    if (min === max) return this.formatter.format(min)
    return `${this.formatter.format(min)} – ${this.formatter.format(max)}`
  }

  // Optionale PLZ-Zuordnung (nur Badumbau): PLZ → Bundesland via /api/zip/:plz,
  // dann Kurzinfo zum Landesförderprogramm aus data.landesfoerderung anzeigen.
  async lookupPlz() {
    const plz = this.plzInputTarget.value.trim()
    if (!/^\d{5}$/.test(plz)) {
      this.renderPlzResult(null)
      return
    }

    try {
      const response = await fetch(`/api/zip/${plz}`)
      if (!response.ok) return
      const data = await response.json()
      this.renderPlzResult(data.valid ? data : false)
    } catch (_) { /* network error — leave the box unchanged */ }
  }

  renderPlzResult(zip) {
    if (!this.hasPlzResultTarget) return
    const box = this.plzResultTarget

    if (zip === null) {
      box.classList.add("hidden")
      return
    }
    if (zip === false) {
      box.classList.remove("hidden")
      box.textContent = "Postleitzahl nicht gefunden — bitte prüfen Sie die Eingabe."
      return
    }
    // Edge-Cache-Antworten von vor dem bundesland-Feld: gültige PLZ, aber
    // keine Zuordnung möglich — dann lieber nichts anzeigen als eine Fehlermeldung.
    if (!zip.bundesland) {
      box.classList.add("hidden")
      return
    }

    const program = (this.dataValue.landesfoerderung || {})[zip.bundesland]
    box.classList.remove("hidden")
    if (!program || program.art === "keins") {
      box.innerHTML = `<strong>${zip.bundesland}:</strong> kein eigenes Landesprogramm für Privathaushalte — ` +
        `es bleiben Pflegekasse, KfW und Steuerwege. Details: <a href="#landesfoerderung" class="underline font-semibold">Landesförderung</a>.`
    } else {
      box.innerHTML = `<strong>Zusätzlich in ${zip.bundesland}:</strong> ${program.bank} fördert mit ` +
        `${program.betrag} (${program.art_label}). Details und Bedingungen: ` +
        `<a href="#landesfoerderung" class="underline font-semibold">Landesförderung</a>.`
    }
  }
}
